import React, { useEffect, useRef } from 'react';
import styled from 'styled-components';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { skills } from '../../data/constants';

gsap.registerPlugin(ScrollTrigger);

const Section = styled.section`
  padding: 110px 0;
  background: ${({ theme }) => theme.bg};
  overflow: hidden;
`;

const Header = styled.div`
  text-align: center;
  margin-bottom: 64px;
  padding: 0 5vw;
`;

const Label = styled.p`
  font-family: 'Space Grotesk', monospace;
  font-size: 12px;
  font-weight: 500;
  letter-spacing: 0.22em;
  text-transform: uppercase;
  color: ${({ theme }) => theme.primary};
  margin-bottom: 14px;
`;

const Title = styled.h2`
  font-family: 'Space Grotesk', sans-serif;
  font-size: clamp(36px, 5vw, 56px);
  font-weight: 700;
  color: ${({ theme }) => theme.text_primary};
  letter-spacing: -0.03em;
`;

const MarqueeTrack = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 20px;

  &::before, &::after {
    content: '';
    position: absolute;
    top: 0;
    bottom: 0;
    width: 160px;
    z-index: 2;
    pointer-events: none;
  }

  &::before {
    left: 0;
    background: linear-gradient(to right, ${({ theme }) => theme.bg}, transparent);
  }

  &::after {
    right: 0;
    background: linear-gradient(to left, ${({ theme }) => theme.bg}, transparent);
  }
`;

const MarqueeRow = styled.div`
  display: flex;
  width: max-content;
  animation: ${({ $dir }) => $dir === 'right' ? 'marquee-right' : 'marquee-left'}
    ${({ $dur }) => $dur || 40}s linear infinite;
  gap: 16px;

  &:hover {
    animation-play-state: paused;
  }
`;

const Pill = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px 28px;
  border-radius: 100px;
  background: ${({ theme }) => theme.card};
  border: 1px solid ${({ theme }) => theme.border};
  white-space: nowrap;
  font-size: 16px;
  font-weight: 500;
  color: ${({ theme }) => theme.text_secondary};
  transition: border-color 0.2s, color 0.2s, transform 0.2s;
  cursor: default;

  &:hover {
    border-color: ${({ theme }) => theme.primary};
    color: ${({ theme }) => theme.text_primary};
    transform: scale(1.04);
  }

  img {
    width: 26px;
    height: 26px;
    object-fit: contain;
  }
`;

const Skills = () => {
  const headerRef = useRef(null);

  useEffect(() => {
    const ctx = gsap.context(() => {
      gsap.fromTo(headerRef.current,
        { y: 40, opacity: 0 },
        {
          y: 0, opacity: 1, duration: 0.8, ease: 'power3.out',
          scrollTrigger: { trigger: headerRef.current, start: 'top 85%' }
        }
      );
    });
    return () => ctx.revert();
  }, []);

  const allSkills = skills.flatMap(cat => cat.skills);
  const half = Math.ceil(allSkills.length / 2);
  const row1 = allSkills.slice(0, half);
  const row2 = allSkills.slice(half);
  const dupe = (arr) => [...arr, ...arr, ...arr];

  return (
    <Section id="skills">
      <Header ref={headerRef}>
        <Label>Expertise</Label>
        <Title>What I Work With</Title>
      </Header>

      <MarqueeTrack>
        <MarqueeRow $dir="left" $dur={36}>
          {dupe(row1).map((skill, i) => (
            <Pill key={i}>
              {skill.image && <img src={skill.image} alt={skill.name} />}
              {skill.name}
            </Pill>
          ))}
        </MarqueeRow>

        <MarqueeRow $dir="right" $dur={42}>
          {dupe(row2).map((skill, i) => (
            <Pill key={i}>
              {skill.image && <img src={skill.image} alt={skill.name} />}
              {skill.name}
            </Pill>
          ))}
        </MarqueeRow>
      </MarqueeTrack>
    </Section>
  );
};

export default Skills;
